/**
 * Web service for default liability waiver operations
 * Pre-configured with web Supabase client
 *
 * Default waivers are stored as HTML with {{placeholder}} variables
 * in the default_waiver_templates database table.
 */

import type { DefaultWaiverTemplate, DefaultWaiverTemplateInsert } from '../types/damageWaiver.types';

import { supabase } from '../lib/supabase';

/**
 * Get the latest active default waiver template from the database.
 */
export async function getLatestDefaultWaiver(): Promise<DefaultWaiverTemplate | null> {
  const { data, error } = await supabase
    .from('default_waiver_templates')
    .select('*')
    .eq('is_active', true)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  return data as DefaultWaiverTemplate;
}

/**
 * Apply the latest default waiver template to a company.
 * Calls a SECURITY DEFINER RPC to bypass the JWT company_id requirement in the
 * damage_waiver_templates INSERT policy, which fails during initial onboarding
 * because the JWT is issued before last_selected_company is set (issue #648).
 */
export async function applyDefaultWaiverToCompany(
  companyId: string
): Promise<{ id: string; name: string; template_type: string }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('apply_default_waiver_to_company', {
    p_company_id: companyId,
  });

  if (error) {
    throw new Error(error.message || 'Failed to apply default waiver');
  }

  return data as { id: string; name: string; template_type: string };
}

/**
 * Check if a company already has any waiver template.
 */
export async function companyHasWaiverTemplate(
  companyId: string
): Promise<boolean> {
  const { count, error } = await supabase
    .from('damage_waiver_templates')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId);

  if (error) return false;
  return (count ?? 0) > 0;
}

// ============================================================================
// Admin CRUD for default_waiver_templates
// ============================================================================

/**
 * List all default waiver template versions (ordered newest first).
 */
export async function getDefaultWaiverTemplates(): Promise<DefaultWaiverTemplate[]> {
  const { data, error } = await supabase
    .from('default_waiver_templates')
    .select('*')
    .order('version', { ascending: false });

  if (error) throw new Error(`Failed to load default waiver templates: ${error.message}`);
  return (data ?? []) as DefaultWaiverTemplate[];
}

/**
 * Get a single default waiver template by ID.
 */
export async function getDefaultWaiverTemplate(id: string): Promise<DefaultWaiverTemplate | null> {
  const { data, error } = await supabase
    .from('default_waiver_templates')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw new Error(`Failed to load default waiver template: ${error.message}`);
  return data as DefaultWaiverTemplate | null;
}

/**
 * Create a new default waiver template version.
 */
export async function createDefaultWaiverTemplate(
  input: DefaultWaiverTemplateInsert
): Promise<DefaultWaiverTemplate> {
  const { data, error } = await supabase
    .from('default_waiver_templates')
    .insert(input)
    .select()
    .single();

  if (error) throw new Error(`Failed to create default waiver template: ${error.message}`);
  return data as DefaultWaiverTemplate;
}

/**
 * Update an existing default waiver template.
 */
export async function updateDefaultWaiverTemplate(
  id: string,
  updates: Partial<DefaultWaiverTemplateInsert>
): Promise<DefaultWaiverTemplate> {
  const { data, error } = await supabase
    .from('default_waiver_templates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update default waiver template: ${error.message}`);
  return data as DefaultWaiverTemplate;
}

/**
 * Calculate the next version number based on existing templates.
 */
export async function getNextDefaultWaiverVersion(): Promise<string> {
  const { data, error } = await supabase
    .from('default_waiver_templates')
    .select('version')
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return '1.0';

  const [major, minor] = data.version.split('.').map(Number);
  return `${major}.${(minor || 0) + 1}`;
}
